import React from "react";
import { Row, Col, Card, CardHeader, CardBody, FormGroup, Input, Label } from "reactstrap";

// ─── Single approval radio group ─────────────────────────────────────────────
const ApprovalRadios = ({ name, value, disabled, onChange }) => (
    <div className="d-flex flex-column gap-1">
        {[
            { val: true, label: "Approved", cls: "text-success" },
            { val: null, label: "Pending", cls: "text-warning" },
            { val: false, label: "Denied", cls: "text-danger" },
        ].map(({ val, label, cls }) => (
            <FormGroup check key={label} className="mb-0">
                <Input
                    type="radio"
                    name={name}
                    checked={JSON.stringify(value) === JSON.stringify(val)}
                    disabled={disabled}
                    onChange={() => onChange(val)}
                />
                <Label check className={`fw-medium ${cls}`} style={{ fontSize: 13 }}>
                    {label}
                </Label>
            </FormGroup>
        ))}
    </div>
);

// ─── Approval panel (Supervisor / HR / MPC) ───────────────────────────────────
const ApprovalPanel = ({
    form,
    setField,
    canEditSupervisor,
    canEditHR,
    canEditMPC,
    showMPC = true,
    showExempt = false,
}) => (
    <Row className="g-2">

        {/* Supervisor */}
        <Col md={showMPC ? 4 : 6}>
            <Card className="border h-100">
                <CardHeader className="py-2 bg-light border-bottom">
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#495057" }}>Supervisor</span>
                </CardHeader>
                <CardBody className="py-2 px-3">
                    <ApprovalRadios
                        name="supervisorApproval"
                        value={form.supervisorApproval}
                        disabled={!canEditSupervisor}
                        onChange={(v) => setField("supervisorApproval", v)}
                    />
                    <div className="mt-2">
                        <Label className="text-muted mb-1" style={{ fontSize: 11 }}>Note</Label>
                        <Input
                            type="textarea" bsSize="sm" rows={2}
                            value={form.supervisorNote ?? ""}
                            disabled={!canEditSupervisor}
                            placeholder={!canEditSupervisor ? "—" : "Add note..."}
                            onChange={(e) => setField("supervisorNote", e.target.value)}
                        />
                    </div>
                </CardBody>
            </Card>
        </Col>

        {/* Human Resources */}
        <Col md={showMPC ? 4 : 6}>
            <Card className="border h-100">
                <CardHeader className="py-2 bg-light border-bottom">
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#495057" }}>Human Resources</span>
                </CardHeader>
                <CardBody className="py-2 px-3">
                    <ApprovalRadios
                        name="hrApproval"
                        value={form.hrApproval}
                        disabled={!canEditHR}
                        onChange={(v) => setField("hrApproval", v)}
                    />
                    <div className="mt-2">
                        <Label className="text-muted mb-1" style={{ fontSize: 11 }}>Note</Label>
                        <Input
                            type="textarea" bsSize="sm" rows={2}
                            value={form.hrNote ?? ""}
                            disabled={!canEditHR}
                            placeholder={!canEditHR ? "—" : "Add note..."}
                            onChange={(e) => setField("hrNote", e.target.value)}
                        />
                    </div>
                    {/* HR override (2026-07, owner request): when the
                        supervisor hasn't decided yet, HR can record its
                        decision on the supervisor slot too so the request
                        doesn't stay stuck. The server refuses this for
                        non-HR callers and never overwrites a real
                        supervisor decision. */}
                    {canEditHR && form.supervisorApproval == null && (
                        <FormGroup check className="mt-2 p-2 rounded"
                                   style={{ background: "#fff8e6", border: "0.5px solid #ffe1a8" }}>
                            <Input
                                type="checkbox" id="hrOverrideSupervisor"
                                checked={!!form.hrOverrideSupervisor}
                                onChange={(e) => setField("hrOverrideSupervisor", e.target.checked)}
                            />
                            <Label check htmlFor="hrOverrideSupervisor" style={{ fontSize: 12 }}>
                                Supervisor unavailable — also record my decision
                                as the supervisor decision (override)
                            </Label>
                        </FormGroup>
                    )}
                </CardBody>
            </Card>
        </Col>

        {/* MPC — hidden when showMPC=false (COA form) */}
        {showMPC && (
            <Col md={4}>
                <Card className="border h-100">
                    <CardHeader className="py-2 bg-light border-bottom">
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#495057" }}>
                            MPC (Leave of Absence)
                        </span>
                    </CardHeader>
                    <CardBody className="py-2 px-3">
                        <ApprovalRadios
                            name="mpcApproval"
                            value={form.mpcApproval}
                            disabled={!canEditMPC}
                            onChange={(v) => setField("mpcApproval", v)}
                        />
                        {showExempt && (
                            <FormGroup check className="mt-2 mb-0">
                                <Input
                                    type="checkbox"
                                    id="chkExempt"
                                    checked={form.exemption ?? false}
                                    disabled={!canEditMPC && !canEditHR}
                                    onChange={(e) => setField("exemption", e.target.checked)}
                                />
                                <Label check htmlFor="chkExempt" style={{ fontSize: 13 }}>
                                    Exempt from FTO deduction
                                </Label>
                            </FormGroup>
                        )}
                    </CardBody>
                </Card>
            </Col>
        )}

    </Row>
);

export default ApprovalPanel;